import React, { useCallback, useEffect, useRef } from "react";
import { FiRotateCcw, FiType } from "react-icons/fi";

const BOLD_COLOR = "#005CA9";

/** Strip bold, underline, italic from HTML, keep text and <br>. */
function stripFormatting(html: string): string {
  if (typeof html !== "string") return "";
  return html.replace(/<\/?(?:strong|b|u|i|em)\b[^>]*>/gi, "");
}

export type ParagraphRichEditorProps = {
  value: string;
  onChange: (html: string) => void;
  minHeight?: string;
  placeholder?: string;
  className?: string;
};

/** Toolbar + contenteditable for bold (#005CA9) and underline. */
export const ParagraphRichEditor: React.FC<ParagraphRichEditorProps> = ({
  value,
  onChange,
  minHeight = "120px",
  placeholder = "Type here…",
  className = "",
}) => {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value) {
      ref.current.innerHTML = value;
    }
  }, [value]);

  const applyFormat = useCallback((command: "bold" | "underline") => {
    document.execCommand(command, false);
    ref.current && onChange(ref.current.innerHTML);
  }, [onChange]);

  const resetStyles = useCallback(() => {
    if (!ref.current) return;
    const plain = stripFormatting(ref.current.innerHTML);
    ref.current.innerHTML = plain;
    onChange(plain);
  }, [onChange]);

  return (
    <div className={`flex min-h-0 flex-col ${className}`.trim()}>
      <div className="mb-2 shrink-0 flex flex-wrap items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
        <button
          type="button"
          onClick={() => applyFormat("bold")}
          className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-semibold transition hover:bg-white hover:shadow"
          title="Bold (#005CA9)"
        >
          <FiType className="h-4 w-4" />
          <span style={{ color: BOLD_COLOR }}>B</span>
        </button>
        <button
          type="button"
          onClick={() => applyFormat("underline")}
          className="rounded-md px-2.5 py-1.5 text-sm font-semibold underline transition hover:bg-white hover:shadow"
          title="Underline"
        >
          U
        </button>
        <button
          type="button"
          onClick={resetStyles}
          className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-white hover:shadow"
          title="Reset all styles"
        >
          <FiRotateCcw className="h-4 w-4" />
          Reset styles
        </button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        style={{ minHeight }}
        className="min-h-0 flex-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-slate-400 [&_strong]:font-semibold [&_strong]:text-[#005CA9] [&_b]:font-semibold [&_b]:text-[#005CA9]"
        onInput={() => ref.current && onChange(ref.current.innerHTML)}
      />
    </div>
  );
};
